/**
 * Filtering the providers list. Pure, in its own module: LLMSettings.tsx pulls
 * in the router, so logic tested through the page needs mocks it does not
 * need on its own.
 *
 * "Usable" is the question the table is really read for — a provider the house
 * can actually run on is shared, enabled, and has a key (or a host, for a
 * self-hosted Ollama). It mirrors `pool.house_block_reason` on the backend;
 * that one decides, this one only filters.
 */
import type { LLMProvider } from '../../types';

export type SharingFilter = '' | 'shared' | 'personal';
export type StateFilter = '' | 'enabled' | 'paused';

export interface ProviderFilters {
  text: string;
  kind: string;
  sharing: SharingFilter;
  state: StateFilter;
}

export const EMPTY_PROVIDER_FILTERS: ProviderFilters = {
  text: '',
  kind: '',
  sharing: '',
  state: '',
};

export function hasProviderFilters(f: ProviderFilters): boolean {
  return !!(f.text.trim() || f.kind || f.sharing || f.state);
}

/** Does this provider have what it needs to make a call? */
export function hasCredentials(p: LLMProvider): boolean {
  return p.llm_interface_type === 'CHAT_OLLAMA' ? !!p.base_url : !!p.has_api_key;
}

export function filterProviders(rows: LLMProvider[], f: ProviderFilters): LLMProvider[] {
  const q = f.text.trim().toLowerCase();
  return rows.filter((p) => {
    if (f.kind && p.llm_interface_type !== f.kind) return false;
    if (f.sharing === 'shared' && !p.is_system) return false;
    if (f.sharing === 'personal' && p.is_system) return false;
    // `enabled` is optional on older payloads; absent means enabled.
    const enabled = p.enabled !== false;
    if (f.state === 'enabled' && !enabled) return false;
    if (f.state === 'paused' && enabled) return false;
    if (q) {
      const hay = [p.name, p.base_url, p.llm_interface_type].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** "3 of 11" for the list header — only worth saying when something is filtered. */
export function providerCountLabel(shown: number, total: number, f: ProviderFilters): string {
  if (!hasProviderFilters(f)) return `${total} provider${total === 1 ? '' : 's'}`;
  return `${shown} of ${total}`;
}
